

import { apiGet, apiPost, apiPut, apiPatch, apiDelete, apiUpload, API_BASE_URL } from '../ApiFetch'

// ============================================================================
// Types
// ============================================================================

export type UserRole = 'BUYER' | 'SELLER' | 'BOTH'

export interface RegisterRequest {
  email: string
  password: string
  firstName?: string
  lastName?: string
  displayName?: string
}

export interface LoginRequest {
  email: string
  password: string
}

export interface OAuthCallbackRequest {
  code: string
  redirectUri?: string
  state?: string
  codeVerifier?: string
  returnTo?: string
}

export interface LineUrlRequest {
  redirectUri?: string
  codeChallenge?: string
  codeChallengeMethod?: "S256"
  state?: string
}

export interface UpdateProfileRequest {
  firstName?: string
  lastName?: string
  displayName?: string
  avatar?: string
  bio?: string
  role?: UserRole
  experience?: string
  brands?: string
  sellerDescription?: string
  isSeller?: boolean
  phone?: string
  location?: string
}

export interface ServiceTag {
  id?: string
  type: string
  value: string
  description?: string
  isActive?: boolean
}

export interface ServicePackage {
  id?: string
  name: string
  description?: string
  price: number
  /** ราคาต่อวันจาก API (ถ้ามี) */
  dailyPrice?: number
  /** ราคาต่อธุรกรรม/งาน จาก API (ถ้ามี) */
  transactionPrice?: number
  features?: unknown
  capacity?: string
  displayOrder?: number
}

export interface Service {
  id?: string
  userId?: string
  creatorId?: string
  serviceCreatorId?: string
  title: string
  description: string
  thumbnail?: string
  tagIds?: string[]
  tags?: ServiceTag[]
  tagRelations?: any[]
  packages: ServicePackage[]
  isActive?: boolean
  completedPurchasesCount?: number
  favoritesCount?: number
  averageRating?: number
  creator?: {
    id?: string
    firstName?: string
    lastName?: string
    displayName?: string
    avatar?: string
  }
  serviceCreator?: {
    id?: string
    firstName?: string
    lastName?: string
    displayName?: string
    avatar?: string
    profile?: {
      avatar?: string
    }
  }
}
 
export interface Order {
  id: string
  serviceId: string
  buyerId: string
  sellerId: string
  amount: number
  status: string
  createdAt: string
  updatedAt: string
  notes?: string
  taskTitle?: string
  taskDescription?: string
  currentSetupStep?: string
  taskId?: string
  package?: {
    id?: string
    name: string
    price: number
    capacity: string
  }
  workspaceId?: string
  workspace?: Workspace
}

export interface WorkspaceTag {
  id?: string
  key?: string
  value?: string
  createdAt?: string
  updatedAt?: string
}

export interface Workspace {
  id: string
  name: string
  storeId?: string
  ownerId?: string
  createdAt?: string
  updatedAt?: string
  imageUrl?: string | null
  tags?: WorkspaceTag[] | null
  /** [ROLE] owned = เจ้าของร้าน, member = ถูกเชิญ */
  access?: 'owned' | 'member'
  role?: string | null
}

export interface CreateWorkspaceRequest {
  name: string
  storeId?: string
  /** Omit when using icon avatar; API rejects null on create */
  imageUrl?: string
}

export interface UpdateWorkspaceRequest
  extends Partial<Omit<CreateWorkspaceRequest, "imageUrl">> {
  /** null clears image when switching to icon avatar */
  imageUrl?: string | null
}

export interface AdminWorkspaceTagKV {
  key: string
  value: string
}

export interface CreateOrderRequest {
  serviceId: string
  packageId: string
  notes?: string
  workspaceId?: string
  taskTitle: string
  taskDescription: string
  // Optional fields for setup tracking
  currentSetupStep?: string
  taskId?: string
}

export interface UpdateOrderStatusRequest {
  status: string
}

export interface UpdateOrderRequest extends Partial<CreateOrderRequest> {
  status?: string
}

export interface UpdateTaskConfigRequest {
  'line-hook'?: {
    channelSecret?: string
    channelAccessToken?: string
  }
}

export interface CreateRatingRequest {
  orderId: string
  rating: number // 1-5
  comment?: string
  // Note: isAnonymous is a frontend-only feature, not in Swagger spec
  isAnonymous?: boolean
}

export interface UpdateRatingRequest {
  rating?: number // 1-5
  comment?: string
  isAnonymous?: boolean
}

export interface LineMessage {
  type: string
  text?: string
  [key: string]: unknown
}

export interface LineSendResponseRequest {
  eventId: string
  messages: LineMessage[]
}

export interface UpdateSetupStepRequest {
  currentSetupStep?: string
  completedSteps?: string[]
  taskId?: string
}

export interface ProductSetupStep {
  id: string
  title?: string
  description?: string
  step_no: number
  step_title: string
  step_description: string
  status: 'PENDING' | 'IN_PROGRESS' | 'COMPLETED'
  completedText?: string
  pendingText?: string
  agent_interactive?: boolean // Added agent_interactive field
  order_required?: boolean
  optional?: boolean
  config?: {
    fields?: Array<{
      name: string
      label: string
      type?: 'text' | 'textarea' | 'select' | 'checkbox'
      options?: Array<{ label: string; value: any }>
      required?: boolean
      placeholder?: string
    }>
    requests?: {
      properties?: Record<string, {
        label?: string
        description?: string
        type?: string
      }>
      required?: string[]
    }
  }
}

export interface ProductSetup {
  productId: string
  steps?: ProductSetupStep[] // Keep for backward compatibility if needed, or remove
  setups: ProductSetupStep[] // New field from API
  currentStepIndex: number
  totalProgress: number
}

// ============================================================================
// Auth API
// ============================================================================

export const authApi = {
  /**
   * Register with email
   */
  register: (data: RegisterRequest) =>
    apiPost(`${API_BASE_URL}/auth/register`, data),

  /**
   * Login with email and password
   */
  login: (data: LoginRequest) =>
    apiPost(`${API_BASE_URL}/auth/login`, data),

  /**
   * Get current user profile
   */
  getMe: () =>
    apiGet(`${API_BASE_URL}/auth/me`),

  /**
   * Google OAuth callback - exchange authorization code for JWT token
   */
  googleCallback: (data: OAuthCallbackRequest) =>
    apiPost(`${API_BASE_URL}/auth/google/callback`, data),

  /**
   * Facebook OAuth callback - exchange authorization code for JWT token
   */
  facebookCallback: (data: OAuthCallbackRequest) =>
    apiPost(`${API_BASE_URL}/auth/facebook/callback`, data),

  /**
   * LINE OAuth callback - exchange authorization code for JWT token
   */
  lineCallback: (data: OAuthCallbackRequest) =>
    apiPost(`${API_BASE_URL}/auth/line/callback`, data),

  /**
   * TikTok OAuth callback - exchange authorization code for JWT token
   */
  tiktokCallback: (data: OAuthCallbackRequest) =>
    apiPost(`${API_BASE_URL}/auth/tiktok/callback`, data),

  /**
   * Get Google OAuth authorization URL
   * Call backend API directly.
   */
  getGoogleUrl: (
    oauthRedirectUri?: string,
    returnTo?: string,
    opts?: {
      forceSelectAccount?: boolean
    },
  ) => {
    const params = new URLSearchParams()
    if (oauthRedirectUri) params.set("oauthRedirectUri", oauthRedirectUri)
    if (returnTo) params.set("returnTo", returnTo)
    if (opts?.forceSelectAccount) params.set("forceSelectAccount", "1")
    const qs = params.toString()
    const url = `${API_BASE_URL}/auth/google/url${qs ? `?${qs}` : ""}`
    return apiGet(url)
  },

  /**
   * Get Facebook OAuth authorization URL
   * Uses Next.js API route which will try backend API first, then fallback to direct URL creation
   */
  getFacebookUrl: (redirectUri?: string) => {
    const url = redirectUri
      ? `/api/auth/facebook/url?redirectUri=${encodeURIComponent(redirectUri)}`
      : `/api/auth/facebook/url`
    return apiGet(url)
  },

  /**
   * Get LINE OAuth authorization URL
   */
  getLineUrl: (params?: LineUrlRequest) => {
    const q = new URLSearchParams()
    if (params?.redirectUri) q.set("redirectUri", params.redirectUri)
    if (params?.codeChallenge) q.set("codeChallenge", params.codeChallenge)
    if (params?.codeChallengeMethod) q.set("codeChallengeMethod", params.codeChallengeMethod)
    if (params?.state) q.set("state", params.state)
    const qs = q.toString()
    const url = `${API_BASE_URL}/auth/line/url${qs ? `?${qs}` : ""}`
    return apiGet(url)
  },

  /**
   * Get TikTok OAuth authorization URL
   */
  getTiktokUrl: () =>
    apiGet(`${API_BASE_URL}/auth/tiktok/url`),
}

// ============================================================================
// Users API
// ============================================================================

export const usersApi = {
  /**
   * Get user by ID
   */
  getUserById: (id: string) =>
    apiGet(`${API_BASE_URL}/users/${id}`),

  /**
   * Update user profile
   */
  updateProfile: (data: UpdateProfileRequest) =>
    apiPut(`${API_BASE_URL}/users/profile`, data),

  /** Avatar only — does not clear seller fields the way PUT /users/profile can. */
  syncAvatar: (avatar: string) =>
    apiPut(`${API_BASE_URL}/users/profile/avatar`, { avatar }),
}

// ============================================================================
// Service Tags API
// ============================================================================

export const serviceTagsApi = {
  /**
   * List all service tags
   */
  list: (params?: { type?: string; isActive?: string; value?: string; limit?: string }) => {
    const queryParams = new URLSearchParams()
    if (params?.type) queryParams.append('type', params.type)
    if (params?.isActive) queryParams.append('isActive', params.isActive)
    if (params?.value) queryParams.append('value', params.value)
    if (params?.limit) queryParams.append('limit', params.limit)
    const query = queryParams.toString()
    return apiGet(`${API_BASE_URL}/service-tags${query ? `?${query}` : ''}`)
  },

  /**
   * Create a new service tag
   */
  create: (data: ServiceTag) =>
    apiPost(`${API_BASE_URL}/service-tags`, data),

  /**
   * Get service tag by ID
   */
  getById: (id: string) =>
    apiGet(`${API_BASE_URL}/service-tags/${id}`),

  /**
   * Update a service tag
   */
  update: (id: string, data: Partial<ServiceTag>) =>
    apiPut(`${API_BASE_URL}/service-tags/${id}`, data),

  /**
   * Delete a service tag
   */
  delete: (id: string) =>
    apiDelete(`${API_BASE_URL}/service-tags/${id}`),
}

// ============================================================================
// Services API
// ============================================================================

export const servicesApi = {
  /**
   * List all services
   */
  list: (params?: {
    creatorId?: string
    tagType?: string
    tagValue?: string
    isActive?: string
    search?: string
    workspaceId?: string
  }) => {
    const queryParams = new URLSearchParams()
    if (params?.creatorId) queryParams.append('creatorId', params.creatorId)
    if (params?.tagType) queryParams.append('tagType', params.tagType)
    if (params?.tagValue) queryParams.append('tagValue', params.tagValue)
    if (params?.isActive) queryParams.append('isActive', params.isActive)
    if (params?.search) queryParams.append('search', params.search)
    if (params?.workspaceId) queryParams.append('workspaceId', params.workspaceId)
    const query = queryParams.toString()
    return apiGet(`${API_BASE_URL}/services${query ? `?${query}` : ''}`)
  },

  /**
   * Create a new service
   */
  create: (data: Service) =>
    apiPost(`${INTEGRATIONS_BASE_URL}/services/`, data),

  /**
   * Get service by ID
   */
  getById: (id: string) =>
    apiGet(`${INTEGRATIONS_BASE_URL}/services/${id}`),

  /**
   * Update a service
   */
  update: (id: string, data: Partial<Service>) =>
    apiPut(`${INTEGRATIONS_BASE_URL}/services/${id}`, data),

  /**
   * Delete a service (soft delete)
   */
  delete: (id: string) =>
    apiDelete(`${INTEGRATIONS_BASE_URL}/services/${id}`),

  /**
   * Add service to favorites
   */
  favorite: (id: string) =>
    apiPost(`${INTEGRATIONS_BASE_URL}/services/${id}/favorite`, {}),

  /**
   * Remove service from favorites
   */
  unfavorite: (id: string) =>
    apiDelete(`${INTEGRATIONS_BASE_URL}/services/${id}/favorite`),
}

// ============================================================================
// Orders API
// ============================================================================

export const ordersApi = {
  /**
   * List orders
   */
  list: (params?: { status?: string; role?: 'buyer' | 'seller'; workspaceId?: string }) => {
    const queryParams = new URLSearchParams()
    if (params?.status) queryParams.append('status', params.status)
    if (params?.role) queryParams.append('role', params.role)
    if (params?.workspaceId) queryParams.append('workspaceId', params.workspaceId)
    const query = queryParams.toString()
    return apiGet(`${API_BASE_URL}/orders${query ? `?${query}` : ''}`)
  },

  /**
   * Create a new order
   */
  create: (data: CreateOrderRequest) =>
    apiPost(`${API_BASE_URL}/orders/`, data),

  /**
   * Get order by ID
   */
  getById: async (id: string) => {
    const response = await apiGet(`${API_BASE_URL}/orders/${id}`) as any
    return response.order || response
  },

  /**
   * Get order by workspaceId and serviceId.
   * Returns `null` when the API responds 404 (no order for this workspace+service).
   */
  getByWorkspaceAndService: async (workspaceId: string, serviceId: string) => {
    const response = (await apiGet(`${API_BASE_URL}/orders/workspace/${workspaceId}/${serviceId}`, {
      nullOn404: true,
    })) as any
    if (response == null) return null
    return response.order || response
  },

  /**
   * Update order (generic)
   */
  update: (id: string, data: UpdateOrderRequest | any) =>
    apiPut(`${API_BASE_URL}/orders/${id}`, data),

  /**
   * Update order status
   */
  updateStatus: (id: string, data: UpdateOrderStatusRequest) =>
    apiPut(`${API_BASE_URL}/orders/${id}/status`, data),

  /**
   * Get task information for an order
   */
  getTask: (id: string) =>
    apiGet(`${API_BASE_URL}/orders/${id}/task`),

  /**
   * Update task configuration
   */
  updateTask: (id: string, data: UpdateTaskConfigRequest) =>
    apiPut(`${API_BASE_URL}/orders/${id}/task`, data),

  /**
   * Get task configuration schema and current values
   */
  getTaskConfig: (id: string) =>
    apiGet(`${API_BASE_URL}/orders/${id}/task/config`),

  /**
   * Sync task status from Docker containers
   */
  syncTask: (id: string) =>
    apiPost(`${API_BASE_URL}/orders/${id}/task/sync`, {}),

  /**
   * Set current setup step for an order
   */
  updateSetupStep: (id: string, data: UpdateSetupStepRequest) =>
    apiPut(`${API_BASE_URL}/orders/${id}/setup-step`, data),
}

// ============================================================================
// Workspaces API
// ============================================================================

export const workspacesApi = {
  /**
   * Create a workspace
   */
  create: (data: CreateWorkspaceRequest) =>
    apiPost(`${API_BASE_URL}/workspaces/`, data),

  /**
   * Update a workspace
   */
  update: (id: string, data: UpdateWorkspaceRequest) =>
    apiPatch(`${API_BASE_URL}/workspaces/${id}`, data),

  tags: {
    /**
     * List tags for a workspace (owner or member)
     */
    list: (workspaceId: string, options?: import("../ApiFetch").ApiFetchOptions) =>
      apiGet(`${API_BASE_URL}/workspaces/${workspaceId}/tags`, options),

    /**
     * Create workspace tag(s) (owner). Accepts one {key,value} or array.
     */
    create: (
      workspaceId: string,
      body: { key: string; value: string } | Array<{ key: string; value: string }>,
    ) => apiPost(`${API_BASE_URL}/workspaces/${workspaceId}/tags`, body),

    /**
     * Update a workspace tag (owner)
     */
    update: (workspaceId: string, tagId: string, body: { key?: string; value?: string }) =>
      apiPatch(`${API_BASE_URL}/workspaces/${workspaceId}/tags/${tagId}`, body),

    /**
     * Delete a workspace tag by key (owner)
     */
    deleteByKey: (workspaceId: string, key: string, value?: string) => {
      const q = new URLSearchParams({ key })
      if (value) q.set('value', value)
      return apiDelete(`${API_BASE_URL}/workspaces/${workspaceId}/tags?${q.toString()}`)
    },
  },

  /**
   * List workspaces
   */
  list: () =>
    apiGet(`${API_BASE_URL}/workspaces/`),

  /**
   * Get workspace by ID
   */
  getById: (id: string) =>
    apiGet(`${API_BASE_URL}/workspaces/${id}`),

  /**
   * Filter workspaces by service ID
   */
  filterByService: (serviceId: string) =>
    apiGet(`${API_BASE_URL}/workspaces/filter/${serviceId}`),

  /**
   * Get latest workspaceId for a specific service.
   * Tries dedicated endpoint first; falls back to filter+sort if unavailable.
   */
  getLastCreatedWorkspaceIdByService: async (serviceId: string): Promise<string | null> => {
    // Preferred backend endpoint: /workspaces/last/:serviceId
    const latestResponse = await apiGet(
      `${API_BASE_URL}/workspaces/last/${serviceId}`,
      { nullOn404: true },
    ) as
      | { id?: string; workspaceId?: string; workspace?: { id?: string } }
      | null

    const latestId =
      latestResponse?.id ||
      latestResponse?.workspaceId ||
      latestResponse?.workspace?.id

    if (typeof latestId === 'string' && latestId) return latestId

    // Fallback for environments that only expose /workspaces/filter/:serviceId
    const response = await apiGet(`${API_BASE_URL}/workspaces/filter/${serviceId}`) as
      | Workspace[]
      | { data?: Workspace[]; workspaces?: Workspace[] }
      | null

    const workspaces = Array.isArray(response)
      ? response
      : Array.isArray(response?.data)
        ? response.data
        : Array.isArray(response?.workspaces)
          ? response.workspaces
          : []

    if (workspaces.length === 0) return null

    const sorted = [...workspaces].sort((a, b) => {
      const aTime = a.createdAt ? new Date(a.createdAt).getTime() : 0
      const bTime = b.createdAt ? new Date(b.createdAt).getTime() : 0
      return bTime - aTime
    })

    return sorted[0]?.id ?? null
  },
}

// ============================================================================
// [ROLE] Authz / workspace invites API
// ============================================================================

export type WorkspaceInviteStatus = 'pending' | 'accepted' | 'revoked' | 'expired'

export type WorkspaceInviteRow = {
  id: string
  workspaceId: string
  role: string
  status: WorkspaceInviteStatus
  expiresAt: string
  createdByUserId: string | null
  createdByKind: string
  acceptedByUserId: string | null
  acceptedByDisplayName: string | null
  acceptedAt: string | null
  revokedAt: string | null
  regeneratedFromId: string | null
  createdAt: string
  token?: string
  acceptPath?: string
  acceptUrl?: string | null
}

export type WorkspaceMemberManageRow = {
  membershipId: string
  userId: string
  role: string
  authorityRevision: number
  displayName: string | null
  email: string | null
  isOwner: boolean
  createdAt: string
}

export const authzApi = {
  listMembers: (workspaceId: string) =>
    apiGet(`${API_BASE_URL}/authz/workspaces/${encodeURIComponent(workspaceId)}/members`),

  patchMemberRole: (workspaceId: string, membershipId: string, role: string) =>
    apiPatch(
      `${API_BASE_URL}/authz/workspaces/${encodeURIComponent(workspaceId)}/members/${encodeURIComponent(membershipId)}`,
      { role },
    ),

  removeMember: (workspaceId: string, membershipId: string) =>
    apiDelete(
      `${API_BASE_URL}/authz/workspaces/${encodeURIComponent(workspaceId)}/members/${encodeURIComponent(membershipId)}`,
    ),

  listInvites: (workspaceId: string) =>
    apiGet(`${API_BASE_URL}/authz/workspaces/${encodeURIComponent(workspaceId)}/invites`),

  createInvite: (workspaceId: string, role: string) =>
    apiPost(`${API_BASE_URL}/authz/workspaces/${encodeURIComponent(workspaceId)}/invites`, {
      role,
    }),

  revokeInvite: (workspaceId: string, inviteId: string) =>
    apiPost(
      `${API_BASE_URL}/authz/workspaces/${encodeURIComponent(workspaceId)}/invites/${encodeURIComponent(inviteId)}/revoke`,
      {},
    ),

  regenInvite: (workspaceId: string, inviteId: string) =>
    apiPost(
      `${API_BASE_URL}/authz/workspaces/${encodeURIComponent(workspaceId)}/invites/${encodeURIComponent(inviteId)}/regen`,
      {},
    ),

  previewInvite: (token: string) =>
    apiGet(`${API_BASE_URL}/authz/invites/preview/${encodeURIComponent(token)}`),

  acceptInvite: (token: string) =>
    apiPost(`${API_BASE_URL}/authz/invites/${encodeURIComponent(token)}/accept`, {}),
}

// ============================================================================
// Admin Workspaces API
// ============================================================================

export const adminWorkspacesApi = {
  /**
   * Set workspace tags (admin)
   */
  setTags: (workspaceId: string, tags: AdminWorkspaceTagKV[]) =>
    apiPost(`${API_BASE_URL}/workspaces/${workspaceId}/tags`, tags),
}

// ============================================================================
// Ratings API
// ============================================================================

export const ratingsApi = {
  /**
   * Get ratings for a user
   */
  getByUserId: (userId: string) =>
    apiGet(`${API_BASE_URL}/ratings/user/${userId}`),

  /**
   * Create a rating for an order
   */
  create: (data: CreateRatingRequest) =>
    apiPost(`${API_BASE_URL}/ratings/`, data),

  /**
   * Update a rating
   */
  update: (id: string, data: UpdateRatingRequest) =>
    apiPut(`${API_BASE_URL}/ratings/${id}`, data),
}

// ============================================================================
// Upload API
// ============================================================================

export interface UploadImageResponse {
  success?: boolean
  file?: {
    filename?: string
    url?: string
    size?: number
    mimetype?: string
  }
  url?: string
  imageUrl?: string
  filename?: string
  message?: string
  [key: string]: unknown
}

export const uploadApi = {
  /**
   * Upload an image file with optional category
   * @param file - File or Blob to upload
   * @param filename - Optional filename override
   * @param category - Optional category: 'avatars', 'services', or 'thumbnails'
   */
  uploadImage: (
    file: File | Blob, 
    filename?: string,
    category?: 'avatars' | 'services' | 'thumbnails',
    onProgress?: (percent: number) => void
  ): Promise<UploadImageResponse> => {
    const formData = new FormData()
    // Use the actual filename if available, or a standard extension to help the server with MIME types
    const fileToUpload = file as any
    const finalFilename = filename || fileToUpload.name || 'image.png'
    formData.append('file', file, finalFilename)
    if (category) {
      formData.append('category', category)
    }
    // Use Next.js API route instead of calling backend directly
    return apiUpload<UploadImageResponse>(`${API_BASE_URL}/upload/image`, formData, undefined, onProgress)
  },

  /**
   * Upload a generic file (PDF, CSV, etc.)
   * @param file - File or Blob to upload
   * @param filename - Optional filename override
   * @param category - Optional category
   * @param onProgress - Optional progress callback
   */
  uploadFile: (
    file: File | Blob, 
    filename?: string,
    category?: string,
    onProgress?: (percent: number) => void
  ): Promise<UploadImageResponse> => {
    const formData = new FormData()
    const fileToUpload = file as any
    const finalFilename = filename || fileToUpload.name || 'file.dat'
    formData.append('file', file, finalFilename)
    if (category) {
      formData.append('category', category)
    }
    return apiUpload<UploadImageResponse>(`${API_BASE_URL}/upload/file`, formData, undefined, onProgress)
  },
}

// ============================================================================
// LINE Webhook API
// ============================================================================

export const lineApi = {
  /**
   * Get LINE bind status/code for a service
   * bound=true  → OA info + lineUserId
   * bound=false → one-time code, fullText, deepLink for user to send to LINE OA
   */
  getBindStatus: (serviceId: string) =>
    apiGet(
      `${INTEGRATIONS_BASE_URL}/integrations/line/bind/status?serviceId=${encodeURIComponent(serviceId)}`,
      { skipProxy: true },
    ),

  /**
   * Lookup existing bindings by LINE userId (marketplace integration)
   * Returns `{ bindings: [{ email: string }] }` (shape may vary)
   */
  getBindingsByLineUserId: (lineUserId: string, destination?: string) =>
    apiGet(
      `${INTEGRATIONS_BASE_URL}/integrations/line/bind/by-line-user?lineUserId=${encodeURIComponent(lineUserId)}${destination ? `&destination=${encodeURIComponent(destination)}` : ""}`,
    ),

  /**
   * Directly connect LINE user to the current authenticated user
   * (no bind-code flow)
   */
  connect: (serviceId: string, lineUserId: string) =>
    apiPost(
      `${INTEGRATIONS_BASE_URL}/integrations/line/connect?serviceId=${encodeURIComponent(serviceId)}`,
      { lineUserId }
    ),

  /**
   * Get LINE Webhook status
   */
  getWebhookStatus: () =>
    apiGet(`${INTEGRATIONS_BASE_URL}/line/webhook`),

  /**
   * Send response to LINE user
   */
  sendResponse: (data: LineSendResponseRequest) =>
    apiPost(`${INTEGRATIONS_BASE_URL}/line/response`, data),

  /**
   * Get event metadata
   */
  getEventMetadata: (eventId: string) =>
    apiGet(`${INTEGRATIONS_BASE_URL}/line/event/${eventId}`),
}

// ============================================================================
// Health & Index
// ============================================================================

export const healthApi = {
  /**
   * Get index
   */
  getIndex: () =>
    apiGet(`${API_BASE_URL}/`),

  /**
   * Get health status
   */
  getHealth: () =>
    apiGet(`${API_BASE_URL}/health`),
}

// ============================================================================
// Credits API
// ============================================================================

export interface CreditTopUpRequest {
  amount: number
  note?: string
  paymentReference?: string
}

export interface AdminCreditAdjustRequest {
  amount: number
  note?: string
}

/** POST /credits/product-orders/plan */
export interface CreditPlanProductOrderRequest {
  planCode: string
  paymentMethod?: string
  paymentReference?: string
  paymentProof?: unknown
  externalPaymentId?: string
}

/** POST /credits/product-orders/topup */
export interface CreditTopupBundleOrderRequest {
  bundleCode: string
  paymentMethod?: string
  paymentReference?: string
  paymentProof?: unknown
  externalPaymentId?: string
}

/** GET /credits/catalog — overpay conversion policy (from CREDIT_OVERPAY_CONVERSION_STRATEGY) */
export interface CreditOverpayConversionCatalog {
  strategy: 'most_expensive_rate' | 'best_fit_package'
  rateThbPerCredit: string
  bundleCode: string
  exampleBonusCreditsPer100Thb: string
  descriptionTh?: string
  /** True when no top-up bundles — overpay grants 0 bonus credits. */
  fallback?: boolean
}

/** POST /credits/product-orders/{id}/report-payment */
export interface CreditReportPaymentRequest {
  payerName: string
  bankName: string
  paidAmount: string | number
  paidAtLocal: string
  slipFile?: string
  slipPreviewUrl?: string
}

const INTEGRATIONS_BASE_URL = API_BASE_URL

/** Same host as market API (credits live on main API) */
const CREDITS_BASE_URL = API_BASE_URL

export const creditsApi = {
  /**
   * Get current credit balance
   */
  getBalance: () => apiGet(`${CREDITS_BASE_URL}/credits/`),

  /**
   * List active monthly plans and top-up bundles (storefront)
   * No authentication required
   */
  getCatalog: () => apiGet(`${CREDITS_BASE_URL}/credits/catalog`),

  /**
   * List credit ledger (transaction history) with optional cursor-based pagination
   */
  getTransactions: (params?: { cursor?: string; limit?: string }) => {
    const queryParams = new URLSearchParams()
    if (params?.cursor) queryParams.append('cursor', params.cursor)
    if (params?.limit) queryParams.append('limit', params.limit)
    const query = queryParams.toString()
    return apiGet(
      `${CREDITS_BASE_URL}/credits/transactions${query ? `?${query}` : ''}`,
    )
  },

  /**
   * Top up user credits
   */
  topUp: (data: CreditTopUpRequest) =>
    apiPost(`${CREDITS_BASE_URL}/credits/topup`, data),

  /**
   * Current plan wallet + active monthly plan purchase (not ledger)
   */
  getPlan: () => apiGet(`${CREDITS_BASE_URL}/credits/plan`),

  /**
   * History of monthly plan purchases (UserCreditPlanPurchase)
   */
  getPurchasesPlans: (params?: { cursor?: string; limit?: string }) => {
    const queryParams = new URLSearchParams()
    if (params?.cursor) queryParams.append('cursor', params.cursor)
    if (params?.limit) queryParams.append('limit', params.limit)
    const query = queryParams.toString()
    return apiGet(
      `${CREDITS_BASE_URL}/credits/purchases/plans${query ? `?${query}` : ''}`,
    )
  },

  /**
   * History of top-up bundle purchases (UserCreditTopupPurchase)
   */
  getPurchasesTopups: (params?: { cursor?: string; limit?: string }) => {
    const queryParams = new URLSearchParams()
    if (params?.cursor) queryParams.append('cursor', params.cursor)
    if (params?.limit) queryParams.append('limit', params.limit)
    const query = queryParams.toString()
    return apiGet(
      `${CREDITS_BASE_URL}/credits/purchases/topups${query ? `?${query}` : ''}`,
    )
  },

  /**
   * Create monthly plan purchase order (awaiting approval)
   */
  createPlanProductOrder: (data: CreditPlanProductOrderRequest) =>
    apiPost(`${CREDITS_BASE_URL}/credits/product-orders/plan`, data),

  /**
   * Create top-up bundle purchase order (awaiting approval)
   */
  createTopupProductOrder: (data: CreditTopupBundleOrderRequest) =>
    apiPost(`${CREDITS_BASE_URL}/credits/product-orders/topup`, data),

  /**
   * List my credit product orders
   */
  listProductOrders: (params?: { limit?: string }) => {
    const queryParams = new URLSearchParams()
    if (params?.limit) queryParams.append('limit', params.limit)
    const query = queryParams.toString()
    return apiGet(
      `${CREDITS_BASE_URL}/credits/product-orders${query ? `?${query}` : ''}`,
    )
  },

  /**
   * Get one of my credit product orders
   */
  getProductOrder: (id: string) =>
    apiGet(`${CREDITS_BASE_URL}/credits/product-orders/${id}`),

  /**
   * Cancel my order (only while awaiting approval)
   */
  cancelProductOrder: (id: string) =>
    apiPost(`${CREDITS_BASE_URL}/credits/product-orders/${id}/cancel`, {}),

  /**
   * Report bank transfer + slip URLs; order → payment_reported
   */
  reportProductOrderPayment: (id: string, data: CreditReportPaymentRequest) =>
    apiPost(
      `${CREDITS_BASE_URL}/credits/product-orders/${id}/report-payment`,
      data,
    ),

  /**
   * Admin: adjust user credits (positive to add, negative to subtract)
   */
  adminAdjust: (userId: string, data: AdminCreditAdjustRequest) =>
    apiPost(`${CREDITS_BASE_URL}/credits/admin/adjust/${userId}`, data),
}

// ============================================================================
// Integrations API
// ============================================================================

export interface GoogleSpreadsheetCreateRequest {
  title: string
}

export interface SpreadsheetValue {
  range: string
  values: Array<Array<string | number | boolean | null>>
  valueInputOption?: 'RAW' | 'USER_ENTERED'
}

export const integrationsApi = {
  /**
   * Check Google Drive/Sheets grant status
   * Returns whether the current user has already granted access
   */
  getGoogleStatus: () =>
    apiGet(`${INTEGRATIONS_BASE_URL}/integrations/google/status`),

  /**
   * Create missing AERA/{workspaceId}/ folders + spreadsheets for the current user.
   * Call when status is connected=true (grant OK) but sheet map is empty.
   */
  provisionGoogle: (workspaceIds?: string | string[]) => {
    const ids = Array.isArray(workspaceIds)
      ? workspaceIds.filter(Boolean).join(",")
      : (workspaceIds || "").trim()
    const qs = ids ? `?workspaceIds=${encodeURIComponent(ids)}` : ""
    return apiPost(
      `${INTEGRATIONS_BASE_URL}/integrations/google/provision${qs}`,
      {},
    )
  },

  /**
   * Start Google Drive/Sheets OAuth consent flow
   * Returns a Google OAuth URL — redirect user to this URL to grant access
   * @param returnTo - Optional URL to redirect back to after OAuth completes
   *                   e.g. "/?workspaceId=xxx&serviceId=yyy&source=order"
   * @param opts - Optional LINE deep-link fields (backend must accept these when linking without web JWT)
   */
  connectGoogle: (
    returnTo?: string,
    opts?: {
      lineLinkState?: string
      oauthRedirectUri?: string
      serviceId?: string
      /** Space-delimited OAuth scopes (least privilege). */
      scope?: string
    },
  ) => {
    const body: Record<string, string> = {}
    if (returnTo) body.returnTo = returnTo
    if (opts?.lineLinkState) body.lineLinkState = opts.lineLinkState
    if (opts?.oauthRedirectUri) body.oauthRedirectUri = opts.oauthRedirectUri
    if (opts?.serviceId) body.serviceId = opts.serviceId
    if (opts?.scope) body.scope = opts.scope
    return apiPost(
      `${INTEGRATIONS_BASE_URL}/integrations/google/connect`,
      Object.keys(body).length ? body : {},
    )
  },

  /**
   * Create a new Google Spreadsheet using the stored grant
   * Requires the user to have already granted access via connectGoogle()
   */
  createSpreadsheet: (data: GoogleSpreadsheetCreateRequest) =>
    apiPost(`${INTEGRATIONS_BASE_URL}/integrations/google/spreadsheets`, data),

  /**
   * Update (write) values into a Google Spreadsheet
   * @param spreadsheetId - ID of the spreadsheet to update
   * @param data - range (e.g. "Sheet1!A1:B2"), values array, and optional valueInputOption
   */
  updateSpreadsheetValues: (spreadsheetId: string, data: SpreadsheetValue) =>
    apiPut(`${INTEGRATIONS_BASE_URL}/integrations/google/spreadsheets/${spreadsheetId}/values`, data),
}

// ============================================================================
// Products API
// ============================================================================

const CHAT_API_BASE_URL = process.env.NEXT_PUBLIC_CHAT_API_URL || API_BASE_URL

export const productsApi = {
  /**
   * Get product setup/onboarding status
   */
  getSetup: (productId: string): Promise<ProductSetup> =>
    apiGet(`${CHAT_API_BASE_URL}/v1/products/${productId}/setup`),
}

// ============================================================================
// Support tickets API
// ============================================================================

export type SupportTicketCategory = 'ISSUE' | 'REQUEST' | 'SUGGESTION'
export type SupportTicketVisibility = 'PUBLIC' | 'PRIVATE'
export type SupportTicketStatus =
  | 'OPEN'
  | 'WAITING_USER'
  | 'IN_PROGRESS'
  | 'RESOLVED'
  | 'CLOSED'

export interface SupportTicketListItem {
  publicId: string
  title: string
  category: SupportTicketCategory
  visibility: SupportTicketVisibility
  status: SupportTicketStatus
  displayName: string | null
  replyCount: number
  lastActivityAt: string
  createdAt: string
}

export interface SupportTicketDetail {
  id: string
  publicId: string
  title: string
  body: string
  category: SupportTicketCategory
  visibility: SupportTicketVisibility
  status: SupportTicketStatus
  imageUrls: string[]
  displayName: string | null
  avatarUrl?: string | null
  contactEmail: string | null
  userId: string | null
  isOwner: boolean
  canReply: boolean
  accessToken?: string
  lastActivityAt: string
  createdAt: string
  replies: Array<{
    id: string
    body: string
    imageUrls: string[]
    authorType: 'USER' | 'STAFF' | 'COMMUNITY'
    displayName: string | null
    userId?: string | null
    avatarUrl?: string | null
    staffLabel: string | null
    createdAt: string
  }>
}

export const couponsApi = {
  redeem: (code: string) =>
    apiPost<{
      success: boolean
      data?: {
        creditsGranted: string
        ledgerId: string
        redemptionId: string
        coupon: { code: string; title: string }
      }
      error?: string
      code?: string
    }>(`${API_BASE_URL}/coupons/redeem`, { code }),

  validateCheckout: (body: {
    code: string
    orderKind: 'TOPUP' | 'PLAN'
    productCode: string
  }) =>
    apiPost<{
      success: boolean
      data?: {
        couponId: string
        code: string
        title: string
        originalPriceThb: string
        discountThb: string
        finalPriceThb: string
      }
      error?: string
      code?: string
    }>(`${API_BASE_URL}/coupons/validate-checkout`, body),
}

export const supportApi = {
  listPublic: (params?: {
    category?: SupportTicketCategory
    status?: SupportTicketStatus
    search?: string
    page?: number
    pageSize?: number
  }) => {
    const q = new URLSearchParams()
    if (params?.category) q.set('category', params.category)
    if (params?.status) q.set('status', params.status)
    if (params?.search) q.set('search', params.search)
    if (params?.page) q.set('page', String(params.page))
    if (params?.pageSize) q.set('pageSize', String(params.pageSize))
    const qs = q.toString()
    return apiGet<{ success: boolean; data: { tickets: SupportTicketListItem[]; total: number; page: number; pageSize: number } }>(
      `${API_BASE_URL}/support/tickets${qs ? `?${qs}` : ''}`,
    )
  },

  listMine: () =>
    apiGet<{ success: boolean; data: { tickets: SupportTicketListItem[] } }>(
      `${API_BASE_URL}/support/tickets/mine`,
    ),

  get: (publicId: string, token?: string) => {
    const q = token ? `?token=${encodeURIComponent(token)}` : ''
    return apiGet<{ success: boolean; data: SupportTicketDetail }>(
      `${API_BASE_URL}/support/tickets/${publicId}${q}`,
    )
  },

  create: (body: {
    title: string
    body: string
    category: SupportTicketCategory
    visibility: SupportTicketVisibility
    imageUrls?: string[]
    contactEmail?: string
    displayName?: string
  }) =>
    apiPost<{ success: boolean; data: { publicId: string; accessToken?: string } }>(
      `${API_BASE_URL}/support/tickets`,
      body,
    ),

  reply: (
    publicId: string,
    body: {
      body: string
      imageUrls?: string[]
      displayName?: string
      contactEmail?: string
    },
    token?: string,
  ) => {
    const q = token ? `?token=${encodeURIComponent(token)}` : ''
    return apiPost(`${API_BASE_URL}/support/tickets/${publicId}/replies${q}`, body)
  },

  uploadImage: (file: File | Blob, filename?: string, onProgress?: (percent: number) => void) => {
    const formData = new FormData()
    const fileToUpload = file as File & { name?: string }
    formData.append('file', file, filename || fileToUpload.name || 'image.png')
    return apiUpload<{ success: boolean; file: { url: string } }>(
      `${API_BASE_URL}/upload/support-image`,
      formData,
      undefined,
      onProgress,
    )
  },
}

// ============================================================================
// Transactions sync (billlin Live Mirror via Next.js proxy)
// ============================================================================

export const transactionsSyncApi = {
  /**
   * Rewrite Google Sheet Live Mirror v2 tabs to match web รายรับ/รายจ่าย.
   * Proxies to billlin-agent POST /api/v1/transactions/sync/backfill-sheet
   * (`force_full` defaults true — full rewrite).
   */
  backfillSheet: (
    workspaceId: string,
    opts?: { forceFull?: boolean },
  ) => {
    const qs = new URLSearchParams({
      workspace_id: workspaceId,
      force_full: String(opts?.forceFull ?? true),
    })
    return apiPost(
      `/api/v1/transactions/sync/backfill-sheet?${qs.toString()}`,
      {},
    )
  },

  /**
   * Live Mirror flags + optional workspace sheet sync health (G2 card badge).
   * Proxies to billlin-agent GET /api/v1/transactions/sync/live-mirror-status
   */
  liveMirrorStatus: (workspaceId?: string | null) => {
    const qs = new URLSearchParams()
    if (workspaceId?.trim()) {
      qs.set('workspace_id', workspaceId.trim())
    }
    const suffix = qs.toString() ? `?${qs.toString()}` : ''
    return apiGet(`/api/v1/transactions/sync/live-mirror-status${suffix}`)
  },
}

// ============================================================================
// Export all APIs
// ============================================================================

export const api = {
  auth: authApi,
  users: usersApi,
  serviceTags: serviceTagsApi,
  services: servicesApi,
  orders: ordersApi,
  workspaces: workspacesApi,
  adminWorkspaces: adminWorkspacesApi,
  ratings: ratingsApi,
  upload: uploadApi,
  health: healthApi,
  line: lineApi,
  credits: creditsApi,
  integrations: integrationsApi,
  products: productsApi,
  support: supportApi,
  coupons: couponsApi,
  transactionsSync: transactionsSyncApi,
}

export default api

